import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

const GUEST_CART_KEY = 'guest_shopping_cart'; // localStorage key for guest users

export interface CartItem {
  id: string;
  item_type: 'product' | 'accommodation';
  product_id?: string;
  accommodation_id?: string;
  variation_id?: string;
  name: string;
  price: number;
  currency: string;
  quantity: number;
  check_in_date?: string;
  check_out_date?: string;
  guest_info?: any;
  category?: string;
  product?: {
    name: string;
    price: number;
    category?: string;
    early_bird_price?: number;
    early_bird_end_date?: string;
    service_date?: string;
    service_time?: string;
    pickup_location?: string;
    dropoff_location?: string;
    equipment_type?: string;
    meeting_point?: string;
    duration_minutes?: number;
  };
  accommodation?: {
    name: string;
    price_per_night: number;
    room_type: string;
  };
  variation?: {
    variation_name: string;
    price: number;
  };
}

interface ShoppingCartContextType {
  cartItems: CartItem[];
  loading: boolean;
  addToCart: (item: Omit<CartItem, 'id'>) => Promise<boolean>;
  removeFromCart: (itemId: string) => Promise<void>;
  updateCartItem: (itemId: string, updates: Partial<CartItem>) => Promise<void>;
  clearCart: () => Promise<void>;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  refreshCart: () => Promise<void>;
  isGuest: boolean;
}

const ShoppingCartContext = createContext<ShoppingCartContextType | undefined>(undefined);

// Guest cart localStorage helpers
const getGuestCart = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveGuestCart = (items: CartItem[]) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch (e) {
    console.error('Failed to save guest cart:', e);
  }
};

const clearGuestCart = () => {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
  } catch (e) {
    console.error('Failed to clear guest cart:', e);
  }
};

export function ShoppingCartProvider({ children }: { children: React.ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isGuest, setIsGuest] = useState(true);
  const { toast } = useToast();

  const fetchCartItems = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      
      if (!user) {
        // Guest user: load from localStorage
        setIsGuest(true);
        const guestCart = getGuestCart();
        setCartItems(guestCart);
        return;
      }

      setIsGuest(false);
      const { data, error } = await supabase
        .from('shopping_cart')
        .select(`
          *,
          product:products(*),
          accommodation:accommodation_options(*),
          variation:product_variations(*)
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setCartItems((data || []).map(item => {
        let displayName = item.product?.name || item.accommodation?.name || 'Unknown Item';
        if (item.variation?.variation_name) {
          displayName = `${displayName} - ${item.variation.variation_name}`;
        }
        
        return {
          ...item,
          item_type: item.product_id ? 'product' : 'accommodation',
          name: displayName,
          category: item.product?.category ?? (item.accommodation ? 'accommodation' : undefined),
          price: item.variation?.price || item.product?.price || item.accommodation?.price_per_night || 0,
          currency: 'USD'
        };
      }));
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const addToCart = async (item: Omit<CartItem, 'id'>): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      
      if (user) {
        // Logged-in user: save to database. Only send real table columns —
        // callers may include helper fields (category, nested product) that
        // aren't columns and would make the insert fail.
        const DB_COLUMNS = ['item_type', 'product_id', 'accommodation_id', 'variation_id', 'name', 'price', 'currency', 'quantity', 'check_in_date', 'check_out_date', 'guest_info', 'session_id'] as const;
        const dbItem = Object.fromEntries(
          Object.entries(item).filter(([k]) => (DB_COLUMNS as readonly string[]).includes(k))
        );
        const { error } = await supabase
          .from('shopping_cart')
          .insert({
            user_id: user.id,
            ...dbItem,
          })
          .select();

        if (error) throw error;
        await fetchCartItems();
      } else {
        // Guest user: save to localStorage
        const guestCart = getGuestCart();
        const newItem: CartItem = {
          ...item,
          id: crypto.randomUUID(),
        };
        guestCart.push(newItem);
        saveGuestCart(guestCart);
        setCartItems(guestCart);
      }

      toast({
        title: 'Added to Cart',
        description: `${item.name} has been added to your cart`,
        action: (
          <ToastAction altText="View cart" onClick={() => {
            // Dispatch a custom event that FloatingCartButton can listen to
            window.dispatchEvent(new CustomEvent('open-cart-modal'));
          }}>
            View Cart
          </ToastAction>
        ),
      });
      return true;
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to add item to cart',
        variant: 'destructive',
      });
      return false;
    }
  };

  const removeFromCart = async (itemId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      
      if (user) {
        // Logged-in user: remove from database
        const { error } = await supabase
          .from('shopping_cart')
          .delete()
          .eq('id', itemId);

        if (error) throw error;
        await fetchCartItems();
      } else {
        // Guest user: remove from localStorage
        const guestCart = getGuestCart();
        const updatedCart = guestCart.filter(item => item.id !== itemId);
        saveGuestCart(updatedCart);
        setCartItems(updatedCart);
      }

      toast({
        title: 'Removed from Cart',
        description: 'Item removed from your cart',
      });
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove item from cart',
        variant: 'destructive',
      });
    }
  };

  const updateCartItem = async (itemId: string, updates: Partial<CartItem>) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      
      if (user) {
        // Logged-in user: update in database
        const { error } = await supabase
          .from('shopping_cart')
          .update(updates)
          .eq('id', itemId);

        if (error) throw error;
        await fetchCartItems();
      } else {
        // Guest user: update in localStorage
        const guestCart = getGuestCart();
        const updatedCart = guestCart.map(item => 
          item.id === itemId ? { ...item, ...updates } : item
        );
        saveGuestCart(updatedCart);
        setCartItems(updatedCart);
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update cart item',
        variant: 'destructive',
      });
    }
  };

  const clearCart = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      
      if (user) {
        // Logged-in user: clear from database
        const { error } = await supabase
          .from('shopping_cart')
          .delete()
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Guest user: clear localStorage
        clearGuestCart();
      }
      
      setCartItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear cart',
        variant: 'destructive',
      });
    }
  };

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => {
      // Variation price takes priority
      if (item.variation?.price != null) {
        return total + (item.variation.price * item.quantity);
      }
      if (item.product) {
        const price = item.product.early_bird_price && 
          new Date() < new Date(item.product.early_bird_end_date || '') 
          ? item.product.early_bird_price 
          : item.product.price;
        return total + (price * item.quantity);
      }
      if (item.accommodation && item.check_in_date && item.check_out_date) {
        const days = Math.ceil(
          (new Date(item.check_out_date).getTime() - new Date(item.check_in_date).getTime()) 
          / (1000 * 60 * 60 * 24)
        );
        return total + (item.accommodation.price_per_night * days);
      }
      // Fallback for guest cart items without nested product/accommodation data
      return total + (item.price * item.quantity);
    }, 0);
  }, [cartItems]);

  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  // Fetch cart on mount and handle auth state changes
  useEffect(() => {
    fetchCartItems();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // User just signed in - sync guest cart to database
        const guestCart = getGuestCart();
        
        if (guestCart.length > 0) {
          // Use setTimeout to avoid deadlock in onAuthStateChange
          setTimeout(async () => {
            try {
              for (const item of guestCart) {
                const { id, ...itemWithoutId } = item;
                await supabase.from('shopping_cart').insert({
                  user_id: session.user.id,
                  ...itemWithoutId,
                });
              }
              clearGuestCart();
              toast({
                title: 'Cart Synced',
                description: 'Your cart items have been saved to your account',
              });
            } catch (e) {
              console.error('Failed to sync guest cart:', e);
            }
            fetchCartItems();
          }, 0);
        } else {
          setTimeout(() => fetchCartItems(), 0);
        }
      } else {
        setTimeout(() => fetchCartItems(), 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchCartItems, toast]);

  return (
    <ShoppingCartContext.Provider
      value={{
        cartItems,
        loading,
        addToCart,
        removeFromCart,
        updateCartItem,
        clearCart,
        getCartTotal,
        getCartItemCount,
        refreshCart: fetchCartItems,
        isGuest,
      }}
    >
      {children}
    </ShoppingCartContext.Provider>
  );
}

export function useShoppingCartContext() {
  const context = useContext(ShoppingCartContext);
  if (context === undefined) {
    throw new Error('useShoppingCartContext must be used within a ShoppingCartProvider');
  }
  return context;
}
